import React, { useState } from 'react';
import {
  getLeads,
  getVideos,
  createVideo,
  updateVideo,
  deleteVideo,
  getParentProfiles,
} from '../lib/supabase';
import { 
  Shield, 
  Users, 
  Mail, 
  Phone, 
  Baby, 
  Calendar,
  Download,
  RefreshCw,
  Eye,
  EyeOff,
  LogOut,
  Plus,
  Video,
  PlaySquare,
  Pencil,
  Trash2,
  Search,
} from 'lucide-react';

const LeadsAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('leads'); // leads | videos | parents

  // Videos
  const [videos, setVideos] = useState([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [videosFilterCategory, setVideosFilterCategory] = useState('');
  const [videosFilterAppropriateness, setVideosFilterAppropriateness] = useState('');
  const [videosSaving, setVideosSaving] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [editingVideoId, setEditingVideoId] = useState(null);
  const [videoForm, setVideoForm] = useState({
    title: '',
    youtube_url: '',
    category: '',
    content_language: '',
    description: '',
    content_appropriateness: '',
  });

  // Parent profiles (read-only)
  const [parentProfiles, setParentProfiles] = useState([]);
  const [parentsLoading, setParentsLoading] = useState(false);

  // Simple password protection - change this password
  const ADMIN_PASSWORD = 'supa';

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
      fetchLeads();
    } else {
      setError('Incorrect password. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    setLeads([]);
    setError('');
  };

  const fetchLeads = async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await getLeads();
      if (result.success) {
        setLeads(result.data);
      } else {
        setError(`Failed to fetch leads: ${result.error}`);
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchVideosData = async (filters = {}) => {
    setVideosLoading(true);
    setError('');

    try {
      const result = await getVideos(filters);
      if (result.success) {
        setVideos(result.data);
      } else {
        setError(`Failed to fetch videos: ${result.error}`);
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setVideosLoading(false);
    }
  };

  const fetchParentProfilesData = async () => {
    setParentsLoading(true);
    setError('');

    try {
      const result = await getParentProfiles();
      if (result.success) {
        setParentProfiles(result.data);
      } else {
        setError(`Failed to fetch parent profiles: ${result.error}`);
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setParentsLoading(false);
    }
  };

  const openCreateVideo = () => {
    setEditingVideoId(null);
    setVideoForm({
      title: '',
      youtube_url: '',
      category: '',
      content_language: '',
      description: '',
      content_appropriateness: '',
    });
    setIsVideoModalOpen(true);
  };

  const openEditVideo = (video) => {
    setEditingVideoId(video.id);
    setVideoForm({
      title: video.title ?? '',
      youtube_url: video.youtube_url ?? '',
      category: video.category ?? '',
      content_language: video.content_language ?? '',
      description: video.description ?? '',
      content_appropriateness: video.content_appropriateness ?? '',
    });
    setIsVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    setEditingVideoId(null);
  };

  const submitVideo = async (e) => {
    e.preventDefault();
    setVideosSaving(true);
    setError('');

    const payload = {
      title: videoForm.title.trim(),
      youtube_url: videoForm.youtube_url.trim(),
      category: videoForm.category.trim(),
      content_language: videoForm.content_language.trim() || null,
      description: videoForm.description.trim() || null,
      content_appropriateness: videoForm.content_appropriateness.trim(),
    };

    try {
      const result = editingVideoId
        ? await updateVideo(editingVideoId, payload)
        : await createVideo(payload);

      if (!result.success) {
        setError(result.error || 'Failed to save video.');
        return;
      }

      closeVideoModal();
      await fetchVideosData({
        category: videosFilterCategory,
        content_appropriateness: videosFilterAppropriateness,
      });
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setVideosSaving(false);
    }
  };

  const confirmDeleteVideo = async (videoId) => {
    const ok = window.confirm('Delete this video? This cannot be undone.');
    if (!ok) return;
    setError('');
    setVideosSaving(true);
    try {
      const result = await deleteVideo(videoId);
      if (!result.success) {
        setError(result.error || 'Failed to delete video.');
        return;
      }
      await fetchVideosData({
        category: videosFilterCategory,
        content_appropriateness: videosFilterAppropriateness,
      });
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setVideosSaving(false);
    }
  };

  const exportToCSV = () => {
    if (leads.length === 0) return;

    const headers = ['Name', 'Email', 'Phone', 'Number of Kids', 'Created At'];
    const csvContent = [
      headers.join(','),
      ...leads.map(lead => [
        `"${lead.name}"`,
        `"${lead.email}"`,
        `"${lead.phone}"`,
        `"${lead.number_of_kids}"`,
        `"${new Date(lead.created_at).toLocaleString()}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `supakid-leads-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Login form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-500 via-yellow-400 to-blue-500 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-md w-full">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <Shield className="w-8 h-8 text-red-500" />
              <h1 className="text-2xl font-bold text-gray-800">SupaKid Admin</h1>
            </div>
            <p className="text-gray-600">Enter password to access leads dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Admin Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 pr-12"
                  placeholder="Enter admin password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl">
                <div className="flex items-center">
                  <div className="text-red-500 mr-2">⚠️</div>
                  <span className="font-medium">{error}</span>
                </div>
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-red-500 to-blue-500 text-white py-3 px-6 rounded-xl font-semibold hover:from-red-600 hover:to-blue-600 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Access Dashboard
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Leads dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8 text-red-500" />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">SupaKid Admin Dashboard</h1>
                <p className="text-sm text-gray-600">Manage videos and view parent subscriptions</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {activeTab === 'leads' && (
                <>
                  <button
                    onClick={fetchLeads}
                    disabled={loading}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    <span>Refresh</span>
                  </button>
                  <button
                    onClick={exportToCSV}
                    disabled={leads.length === 0}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export CSV</span>
                  </button>
                </>
              )}

              {activeTab === 'videos' && (
                <button
                  onClick={() =>
                    fetchVideosData({
                      category: videosFilterCategory,
                      content_appropriateness: videosFilterAppropriateness,
                    })
                  }
                  disabled={videosLoading}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${videosLoading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
              )}

              {activeTab === 'parents' && (
                <button
                  onClick={fetchParentProfilesData}
                  disabled={parentsLoading}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${parentsLoading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
              )}

              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => {
                setActiveTab('leads');
                if (leads.length === 0 && !loading) fetchLeads();
              }}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                activeTab === 'leads'
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              Leads
            </button>
            <button
              onClick={() => {
                setActiveTab('videos');
                if (videos.length === 0 && !videosLoading) {
                  fetchVideosData({
                    category: videosFilterCategory,
                    content_appropriateness: videosFilterAppropriateness,
                  });
                }
              }}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                activeTab === 'videos'
                  ? 'bg-purple-50 border-purple-300 text-purple-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              Videos
            </button>
            <button
              onClick={() => {
                setActiveTab('parents');
                if (parentProfiles.length === 0 && !parentsLoading) fetchParentProfilesData();
              }}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                activeTab === 'parents'
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              Parent Profiles
            </button>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="container mx-auto px-4 py-6">
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-6">
            <div className="flex items-center">
              <div className="text-red-500 mr-2">⚠️</div>
              <span className="font-medium">{error}</span>
            </div>
          </div>
        )}

        {activeTab === 'leads' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <div className="flex items-center">
                  <Users className="w-8 h-8 text-blue-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Leads</p>
                    <p className="text-2xl font-bold text-gray-900">{leads.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <div className="flex items-center">
                  <Baby className="w-8 h-8 text-green-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Kids</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {leads.reduce((sum, lead) => sum + parseInt(lead.number_of_kids) || 0, 0)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <div className="flex items-center">
                  <Calendar className="w-8 h-8 text-purple-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Today's Leads</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {leads.filter((lead) => {
                        const today = new Date().toDateString();
                        const leadDate = new Date(lead.created_at).toDateString();
                        return today === leadDate;
                      }).length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <div className="flex items-center">
                  <Mail className="w-8 h-8 text-yellow-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Unique Emails</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {new Set(leads.map((lead) => lead.email)).size}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-semibold text-gray-800">All Leads</h2>
              </div>
              {loading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">Loading leads...</p>
                </div>
              ) : leads.length === 0 ? (
                <div className="p-8 text-center">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No leads found. Submit the form to see leads here.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Phone
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Kids
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Submitted
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {leads.map((lead, index) => (
                        <tr key={lead.id || index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                {lead.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <Mail className="w-4 h-4 text-gray-400 mr-2" />
                              {lead.email}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <Phone className="w-4 h-4 text-gray-400 mr-2" />
                              {lead.phone}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <Baby className="w-4 h-4 text-gray-400 mr-2" />
                              {lead.number_of_kids}{' '}
                              {lead.number_of_kids === '1' ? 'child' : 'children'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                              {new Date(lead.created_at).toLocaleString()}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'videos' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm border md:col-span-2">
                <div className="flex items-center">
                  <Video className="w-8 h-8 text-purple-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Videos</p>
                    <p className="text-2xl font-bold text-gray-900">{videos.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <div className="flex items-center">
                  <PlaySquare className="w-8 h-8 text-indigo-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Filter Category</p>
                    <p className="text-sm font-bold text-gray-900">
                      {videosFilterCategory.trim() ? videosFilterCategory : 'All'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <div className="flex items-center">
                  <Search className="w-8 h-8 text-pink-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Appropriateness</p>
                    <p className="text-sm font-bold text-gray-900">
                      {videosFilterAppropriateness.trim() ? videosFilterAppropriateness : 'All'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Manage Videos</h2>
                  <p className="text-sm text-gray-600">Add, edit, delete, and filter by category.</p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={openCreateVideo}
                    className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Video</span>
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div className="px-6 py-4 border-b">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Category</label>
                    <input
                      value={videosFilterCategory}
                      onChange={(e) => setVideosFilterCategory(e.target.value)}
                      placeholder="e.g. Math, Stories"
                      className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Appropriateness</label>
                    <input
                      value={videosFilterAppropriateness}
                      onChange={(e) => setVideosFilterAppropriateness(e.target.value)}
                      placeholder="e.g. Toddler 2-4"
                      className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <button
                      onClick={() =>
                        fetchVideosData({
                          category: videosFilterCategory,
                          content_appropriateness: videosFilterAppropriateness,
                        })
                      }
                      disabled={videosLoading}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50"
                    >
                      <Search className="w-4 h-4" />
                      <span>Apply</span>
                    </button>
                  </div>
                  <div className="flex items-end gap-2">
                    <button
                      onClick={() => {
                        setVideosFilterCategory('');
                        setVideosFilterAppropriateness('');
                        fetchVideosData({});
                      }}
                      disabled={videosLoading}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>

              {videosLoading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">Loading videos...</p>
                </div>
              ) : videos.length === 0 ? (
                <div className="p-8 text-center">
                  <Video className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No videos found.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Appropriateness
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Language
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          YouTube
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {videos.map((video) => (
                        <tr key={video.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-gray-900">{video.title}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {video.category}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {video.content_appropriateness}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {video.content_language || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {video.youtube_url ? (
                              <a
                                href={video.youtube_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline"
                              >
                                Link
                              </a>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {video.created_at ? new Date(video.created_at).toLocaleString() : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openEditVideo(video)}
                                disabled={videosSaving}
                                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
                              >
                                <Pencil className="w-4 h-4 text-gray-700" />
                              </button>
                              <button
                                onClick={() => confirmDeleteVideo(video.id)}
                                disabled={videosSaving}
                                className="p-2 rounded-lg border border-red-200 hover:bg-red-50 transition-colors disabled:opacity-50"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === 'parents' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm border md:col-span-2">
                <div className="flex items-center">
                  <Users className="w-8 h-8 text-blue-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Parents</p>
                    <p className="text-2xl font-bold text-gray-900">{parentProfiles.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <div className="flex items-center">
                  <Shield className="w-8 h-8 text-green-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Premium</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {parentProfiles.filter((p) => p.is_premium).length}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-semibold text-gray-800">Parent Profiles (Read Only)</h2>
                <p className="text-sm text-gray-600">View premium status and subscription info.</p>
              </div>

              {parentsLoading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">Loading parent profiles...</p>
                </div>
              ) : parentProfiles.length === 0 ? (
                <div className="p-8 text-center">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No parent profiles found.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Premium
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Expires
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          RevenueCat User ID
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {parentProfiles.map((p) => (
                        <tr key={p.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                            {[p.first_name, p.last_name].filter(Boolean).join(' ') || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {p.email || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                p.is_premium
                                  ? 'bg-green-50 text-green-700 border border-green-200'
                                  : 'bg-gray-50 text-gray-600 border border-gray-200'
                              }`}
                            >
                              {p.is_premium ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {p.subscription_status || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {p.subscription_expires_at
                              ? new Date(p.subscription_expires_at).toLocaleString()
                              : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {p.revenuecat_user_id || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Video create/edit modal */}
      {isVideoModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (!videosSaving) closeVideoModal();
            }}
          />
          <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
            <div className="px-6 py-5 border-b bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold">
                    {editingVideoId ? 'Edit Video' : 'Add Video'}
                  </h3>
                  <p className="text-sm text-purple-100">
                    Manage entries in <span className="font-semibold">public.videos</span>
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (!videosSaving) closeVideoModal();
                  }}
                  className="px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-lg text-white"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="px-6 py-6">
              <form onSubmit={submitVideo} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="text-sm font-semibold text-gray-700">Title *</label>
                  <input
                    value={videoForm.title}
                    onChange={(e) => setVideoForm((v) => ({ ...v, title: e.target.value }))}
                    required
                    className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Video title"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700">YouTube URL *</label>
                  <input
                    value={videoForm.youtube_url}
                    onChange={(e) => setVideoForm((v) => ({ ...v, youtube_url: e.target.value }))}
                    required
                    className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700">Category *</label>
                  <input
                    value={videoForm.category}
                    onChange={(e) => setVideoForm((v) => ({ ...v, category: e.target.value }))}
                    required
                    className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g. Stories"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700">Content Appropriateness *</label>
                  <input
                    value={videoForm.content_appropriateness}
                    onChange={(e) =>
                      setVideoForm((v) => ({ ...v, content_appropriateness: e.target.value }))
                    }
                    required
                    className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g. Toddler 2-4"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700">Content Language</label>
                  <input
                    value={videoForm.content_language}
                    onChange={(e) =>
                      setVideoForm((v) => ({ ...v, content_language: e.target.value }))
                    }
                    className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g. English"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="text-sm font-semibold text-gray-700">Description</label>
                  <textarea
                    value={videoForm.description}
                    onChange={(e) =>
                      setVideoForm((v) => ({ ...v, description: e.target.value }))
                    }
                    className="mt-1 w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows={4}
                    placeholder="Optional description"
                  />
                </div>

                <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={closeVideoModal}
                    disabled={videosSaving}
                    className="px-5 py-2.5 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={videosSaving}
                    className="px-5 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50 font-semibold"
                  >
                    {videosSaving ? 'Saving...' : editingVideoId ? 'Save Changes' : 'Create Video'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsAdmin;
